import type { ReactNode, Ref, UIEventHandler } from 'react'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import { MemberHeroHeader } from './MemberControlsPrimitives'
import { StatusChip } from '../../Common'
import { Pressable } from '../../Common/Pressable'

export type MemberNexusChip = {
  key: string
  label: ReactNode
  value?: ReactNode
  onClick?: () => void
  tone?: 'neutral' | 'live'
  icon?: ReactNode
}

const defaultChipIcon = (key: string): ReactNode => {
  if (key.toLowerCase().includes('calendar') || key.toLowerCase().includes('today')) return <CalendarTodayRoundedIcon fontSize="inherit" />
  if (key.toLowerCase().includes('huddle')) return <SensorsRoundedIcon fontSize="inherit" />
  if (key.toLowerCase().includes('meeting')) return <GroupsRoundedIcon fontSize="inherit" />
  return <ChatBubbleOutlineRoundedIcon fontSize="inherit" />
}

export const MemberNexusHero = ({
  eyebrow,
  title,
  chips,
  rightSlot,
  className,
}: {
  eyebrow: ReactNode
  title: ReactNode
  chips?: MemberNexusChip[]
  rightSlot?: ReactNode
  className?: string
}) => (
  <MemberHeroHeader
    eyebrow={eyebrow}
    title={title}
    className={className}
    chips={rightSlot ?? (chips && chips.length > 0 ? (
      <div className="memberNexusHero__chips">
        {chips.map((chip) => {
          const content = (
            <>
              <span className="memberNexusHero__chipIcon" aria-hidden="true">{chip.icon ?? defaultChipIcon(chip.key)}</span>
              <span>{chip.label}</span>
              {chip.value !== undefined ? <strong className="memberNexusHero__chipBadge">{chip.value}</strong> : null}
            </>
          )
          return chip.onClick ? (
            <Pressable
              key={chip.key}
              className={`memberNexusHero__chip memberTapRipple ${chip.tone === 'live' ? 'isLive' : ''}`}
              onClick={chip.onClick}
            >
              {content}
            </Pressable>
          ) : (
            <StatusChip
              key={chip.key}
              size="sm"
              tone={chip.tone === 'live' ? 'success' : 'neutral'}
              className={`memberNexusHero__chip ${chip.tone === 'live' ? 'isLive' : ''}`}
            >
              {content}
            </StatusChip>
          )
        })}
      </div>
    ) : undefined)}
  />
)

export const MemberSurfaceFrame = ({
  children,
  bottomSlot,
  className,
  testId,
  scrollRef,
  onScroll,
}: {
  children: ReactNode
  bottomSlot?: ReactNode
  className?: string
  testId?: string
  scrollRef?: Ref<HTMLDivElement>
  onScroll?: UIEventHandler<HTMLDivElement>
}) => (
  <section className={`memberSurfaceFrame ${className ?? ''}`} data-testid={testId}>
    <div className="memberSurfaceFrame__scroll" ref={scrollRef} onScroll={onScroll}>
      {children}
    </div>
    {bottomSlot ? <div className="memberSurfaceFrame__bottom">{bottomSlot}</div> : null}
  </section>
)

export const MemberPresenceChip = ({
  count,
  label = 'In huddle',
  onClick,
}: {
  count: number
  label?: string
  onClick?: () => void
}) => {
  const content = (
    <>
      <SensorsRoundedIcon fontSize="inherit" />
      <span>{label}</span>
      <strong>{count}</strong>
    </>
  )
  return onClick ? (
    <Pressable className="memberPresenceLiveChip memberTapRipple" onClick={onClick}>
      {content}
    </Pressable>
  ) : (
    <StatusChip size="sm" tone="success" className="memberPresenceLiveChip">{content}</StatusChip>
  )
}
